import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropType from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import ModalPopup from './ModalPopup';
import { ReactComponent as ChevronLeft } from '../../assets/images/svg/chevron-left.svg';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import IconButton from '../IconButton/IconButton';
import SectionLoader from '../Loader/SectionLoader';
import { closeWizardContent } from '../../util/helpers/wizardHelpers';
import { useOutsideClickHandler } from '../../util/hooks/useOutsideClickHandler';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { uFlexColumn, uTextEllipsis } from '../../assets/styles/utility';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import ModalButtons from './ModalButtons';

const headerHeightDesktop = pxToRem(80);
const headerHeightMobile = pxToRemMd(74);

const getModalSize = (size, isCenterModal) => {
  if (size === 'sm') {
    return `
    max-width:  ${isCenterModal ? pxToRem(780) : pxToRem(390)}};
    width: 100%;

  ${mediaBelow(variables.breakpoints.bpMd)} {
      margin: ${headerHeightMobile} auto ${headerHeightMobile};
      max-height: calc(100vh - (2 * ${headerHeightMobile}));
  }`;
  }

  if (size === 'md') {
    return `
    max-width: ${pxToRem(521)};
     width: 100%;
   

   ${mediaBelow(variables.breakpoints.bpMd)} {
       margin: ${headerHeightMobile} auto ${headerHeightMobile};
       max-height: calc(100vh - (2 * ${headerHeightMobile}));
   }`;
  }

  if (size === 'lg') {
    return `
        max-width: ${pxToRem(782)};
        width: 100%;`;
  }
};

const getModalHeight = (size) => {
  switch (size) {
    case 'sm':
    case 'md':
    case 'lg':
      return `${pxToRem(window.innerHeight - 148)}`;
    default:
      return `${pxToRem(window.innerHeight - 160)}`;
  }
};

export const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${variables.zIndex.indexXl};
  background-color: ${themeColors.colorModalBackground};
  ${(props) => props.css};
  ${({ overModal }) =>
    overModal &&
    `
      background-color: transparent;
      z-index: ${variables.zIndex.indexXxl};
    `};
  ${({ isCenterModal }) => isCenterModal && `backdrop-filter: blur(10px);`}
  ${({ verticalCenter }) =>
    verticalCenter &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

export const ModalElement = styled.div`
  ${uFlexColumn};
  box-shadow: ${variables.shadow.boxShadow};
  ${({ isCenterModal }) =>
    isCenterModal
      ? `border-radius: ${variables.borderRadius.borderRadiusLg}`
      : `border-radius: ${variables.borderRadius.borderRadius}`};
  background-color: ${themeColors.colorWhite};
  ${({ size, isCenterModal, verticalCenter }) => {
    if (verticalCenter) return `margin: 0 auto`;
    if (isCenterModal) return `margin: calc(${getModalHeight(size)} / 4) auto`;
    return `margin: ${headerHeightDesktop} auto ${headerHeightDesktop}`;
  }};
  max-height: calc(100vh - (2 * ${headerHeightDesktop}));
  position: relative;

  ${({ size, isCenterModal }) => getModalSize(size, isCenterModal)}
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  ${({ height }) => height && `height: ${height};`}
  ${({ clipContent }) => clipContent && `overflow: hidden;`}

  ${mediaBelow(variables.breakpoints.bpMd)} {
    ${({ isCenterModal, verticalCenter }) =>
      isCenterModal || verticalCenter
        ? `
          width: calc(100% - ${pxToRemMd(40)});
          max-width: ${pxToRemMd(440)};
          height: auto;
          max-height: calc(100vh - ${pxToRemMd(80)});
          margin: ${pxToRemMd(40)} auto;
        `
        : `
          border-radius: 0;
          width: 100%;
          height: 100%;
          max-height: 100vh;
          max-width: 100vw;
          margin: 0;
        `}
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  ${({ isCenterModal, $bordered }) => {
    if (isCenterModal) return `align-self: center;`;
    // $bordered swaps the default header drop-shadow for a flat bottom border.
    return `
      align-items: center;
      ${
        $bordered
          ? `border-bottom: 1px solid ${themeColors.colorBorderPrimary};`
          : `box-shadow: ${variables.shadow.boxShadow};`
      }
    `;
  }};
  z-index: ${variables.zIndex.indexXxs};
  padding: ${pxToRem(12)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: ${pxToRemMd(12)} ${pxToRemMd(16)};
  }
`;

export const ModalClose = styled(IconButton)`
  color: ${({ $iconColor }) => $iconColor || themeColors.colorTextPrimary};

  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    &,
    svg {
      width: ${pxToRemMd(24)};
      height: ${pxToRemMd(24)};
    }
  }
`;

export const ModalBack = styled(ModalClose)`
  margin-right: ${pxToRem(10)};
`;

export const ModalBodyWrap = styled.div`
  ${uFlexColumn}
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

export const ModalTitle = styled.h3`
  ${uTextEllipsis};
  font-size: ${pxToRem(16)};
  font-weight: 600;
  line-height: 1.5;
  padding-right: ${pxToRem(10)};
  margin-right: auto;
  ${({ isCenterModal }) =>
    isCenterModal &&
    `
      display: flex;
      font-size: 3.5rem;
      margin-top: 16px;
      line-height: 1;
    `};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${({ isCenterModal }) =>
      isCenterModal ? 'font-size: 3.5rem;' : `font-size: ${pxToRemMd(16)};`}
`;

const Modal = ({
  isOpen,
  children,
  goBack,
  close,
  closeIconVisible,
  title,
  size,
  maxWidth,
  minHeight,
  height,
  clipContent,
  isLoading,
  overModal,
  avoidClearingStorage,
  handleOutsideClick,
  skipListeningToLocations,
  popupTitle,
  popupMessage,
  onClosePopup,
  isPopupOpen,
  popUpPrimaryButtonText,
  isCenterModal,
  hideHeader,
  verticalCenter,
  dataCy,
  closeIconColor,
  headerBordered,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.removeAttribute('style');
    };
  }, [isOpen]);

  const closeModal = () => {
    if (!avoidClearingStorage) {
      closeWizardContent();
    }

    if (typeof close === 'function') {
      close();
    }
  };

  useEffect(() => {
    if (history && !skipListeningToLocations) {
      return history.listen(() => closeModal());
    }

    return () => {
      if (history) {
        history.listen(() => undefined);
      }
    };
  }, []); //eslint-disable-line

  const modalRef = useRef(null);

  useOutsideClickHandler(modalRef, close, handleOutsideClick);

  return isOpen
    ? ReactDOM.createPortal(
        <ModalContainer
          overModal={overModal}
          isOpen={isOpen}
          isCenterModal={isCenterModal}
          verticalCenter={verticalCenter}
        >
          <ModalElement
            size={size}
            maxWidth={maxWidth}
            minHeight={minHeight}
            height={height}
            clipContent={clipContent}
            ref={modalRef}
            data-cy={dataCy}
            isCenterModal={isCenterModal}
            verticalCenter={verticalCenter}
          >
            <SectionLoader isLoading={isLoading}>
              {!isPopupOpen && (
                <>
                  {!hideHeader && (
                    <ModalHeader
                      isCenterModal={isCenterModal}
                      $bordered={headerBordered}
                    >
                      {goBack && (
                        <ModalBack onClick={() => goBack()}>
                          <ChevronLeft />
                        </ModalBack>
                      )}
                      {title && (
                        <ModalTitle isCenterModal={isCenterModal}>
                          {title}
                        </ModalTitle>
                      )}
                      {close && closeIconVisible && (
                        <ModalClose
                          onClick={closeModal}
                          data-cy="button-close-modal"
                          $iconColor={closeIconColor}
                        >
                          <Close />
                        </ModalClose>
                      )}
                    </ModalHeader>
                  )}
                  <ModalBodyWrap>{children}</ModalBodyWrap>
                </>
              )}
              <ModalPopup
                title={popupTitle}
                message={popupMessage}
                alignItems="center"
                isPopupOpen={isPopupOpen}
                size={size}
              >
                <ModalButtons
                  showOnlyPrimary
                  primaryButtonProps={{
                    label: popUpPrimaryButtonText,
                    onClick: () =>
                      popupTitle === t('irs.ssn.error.SSNTooManyAttempts.title')
                        ? closeModal()
                        : onClosePopup(),
                  }}
                />
              </ModalPopup>
            </SectionLoader>
          </ModalElement>
        </ModalContainer>,
        document.getElementById('root'),
      )
    : null;
};

Modal.propTypes = {
  children: PropType.node,
  isOpen: PropType.oneOfType([PropType.bool, PropType.shape({})]),
  goBack: PropType.oneOfType([PropType.func, PropType.bool]),
  close: PropType.func,
  closeIconVisible: PropType.bool,
  title: PropType.string,
  size: PropType.oneOf(['sm', 'md', 'lg']),
  maxWidth: PropType.string,
  minHeight: PropType.string,
  height: PropType.string,
  clipContent: PropType.bool,
  className: PropType.string,
  isLoading: PropType.bool,
  avoidClearingStorage: PropType.bool,
  handleOutsideClick: PropType.bool,
  skipListeningToLocations: PropType.bool,
  popupTitle: PropType.string,
  popupMessage: PropType.string,
  popUpPrimaryButtonText: PropType.string,
  onClosePopup: PropType.func,
  isPopupOpen: PropType.bool,
  hideHeader: PropType.bool,
  verticalCenter: PropType.bool,
  dataCy: PropType.string,
  closeIconColor: PropType.string,
  headerBordered: PropType.bool,
};

export default Modal;
